import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Tooltip } from '@material-ui/core';
import SaveAltIcon from '@material-ui/icons/SaveAlt';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Matches the "Manual" button
 * already in the bar, which is the pattern this one follows.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Core
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  button: {
    marginRight: theme.spacing(2),
    whiteSpace: 'nowrap',
  },
}));

/**
 * @function GeoJsonExportButton
 * @description Writes the analysis's vector layers to a GeoJSON file for a GIS.
 * Labelled rather than left as an icon. A bare icon has to carry two things at once here - that
 * this downloads something, and that what comes down is GeoJSON - and no single glyph says both:
 * a download arrow next to the session export's download arrow says nothing about which is which,
 * and a globe reads as "map" or "language" long before it reads as "save the layers". The format
 * name is the one word that settles it, and a tooltip only pays off for someone who already
 * suspected what the button was for.
 * The arrow keeps the download meaning, the word carries the format, and the outlined button
 * matches the "Manual" button beside it, so the bar stays one design rather than two.
 * The format name is written here rather than kept in the language files: GeoJSON is a proper
 * noun, spelled the same in every language, so a key for it could never hold two different values
 * and would only be one more thing to go missing. The tooltip beside it is real prose, and stays
 * translated.
 * Shown only while a result page has registered a builder (see `useRegisterGeoJsonExport`), so a
 * module with nothing vector to offer simply does not show it.
 * @param {Object} props
 * @param {Function} props.onExport - Called on click; builds and downloads the GeoJSON.
 * @see {@link useRegisterGeoJsonExport}
 * **Called by:
 * @see {NavigationBar}
 * @memberof module:Core
 * @returns {JSX.Element}
 */
const GeoJsonExportButton = ({ onExport }) => {
  const classes = useStyles();
  const [t] = useTranslation();

  return (
    <Tooltip title={t('forms.acquisition.geoJsonExport.tooltip')}>
      <Button
        className={classes.button}
        variant="outlined"
        color="primary"
        startIcon={<SaveAltIcon />}
        onClick={onExport}>
        GeoJSON
      </Button>
    </Tooltip>
  );
};

export default GeoJsonExportButton;
